<template>
<div class="outer_box">
  <div class="all_out_box">
    <div class="tttiltleBox">
      <div class="title-container">
        <h1 class="main-title" style="word-break: break-word; white-space: normal; max-width: 100%;">
          <span class="icon-title">🏠</span>
          <span class="house-name-text">{{ (house && house.name) ? house.name : 'Loading listing…' }}</span>
          <template v-if="displayRoomLabel">
            <span class="title-room-sep" aria-hidden="true">·</span>
            <span class="house-room-text">{{ displayRoomLabel }}</span>
          </template>
        </h1>
        <div class="title-underline"></div>
        <p class="subtitle">Create a review</p>
        <p class="draft-hint">
          Your answers are saved automatically in this browser for this listing.
          <button type="button" class="linkish-btn" @click="quickFillRequiredFields">
            Fill empty required fields (neutral values)
          </button>
        </p>
        <div class="room-switcher">
          <label class="switcher-label">Room</label>
          <el-select
            v-model="selectedRoomId"
            class="room-select"
            :loading="roomsLoading"
            placeholder="Select room"
            @change="handleRoomSwitch"
          >
            <el-option
              v-for="room in roomOptions"
              :key="'room-opt-' + roomOptionId(room)"
              :label="roomOptionLabel(room)"
              :value="roomOptionId(room)"
            />
            <el-option
              key="add-room"
              label="Add new room"
              value="__add_room__"
            />
          </el-select>
        </div>
      </div>
    </div>

    <div class="top_rate">
      <div class="top_rate_right">
        <div class="form-section">
          <h3 class="section-title"><span class="section-icon">🏠</span> Stay Context</h3>
          
          <div class="input-group">
            <div class="input-field">
              <label class="form-label">Room Type</label>
              <div class="select-container">
                <select v-model="roomType" class="custom-input modern-input">
                  <option value="" disabled>Select room type</option>
                  <option value="Private">Private</option>
                  <option value="Shared">Shared</option>
                  <option value="Entire Unit">Entire Unit</option>
                </select>
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Stay Duration</label>
              <div class="select-container">
                <select v-model="stayDuration" class="custom-input modern-input">
                  <option value="" disabled>Select duration</option>
                  <option value="1">&lt;3 months</option>
                  <option value="2">3–6 months</option>
                  <option value="3">6–12 months</option>
                  <option value="4">1+ year</option>
                </select>
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Number of Roommates</label>
              <div class="input-container">
                <input
                  v-model="roommatesNum"
                  type="number"
                  min="0"
                  placeholder="Enter number"
                  class="custom-input modern-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title"><span class="section-icon">💰</span> Pricing Details</h3>
          
          <div class="input-group">
            <div class="input-field">
              <label class="form-label">Monthly Rent</label>
              <div class="input-container">
                <span class="input-prefix">HKD</span>
                <input
                  v-model="monthlyRent"
                  type="number"
                  min="0"
                  placeholder="e.g. 5000"
                  class="custom-input modern-input with-prefix"
                />
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Deposit</label>
              <div class="input-container">
                <span class="input-prefix">HKD</span>
                <input
                  v-model="deposit"
                  type="number"
                  min="0"
                  placeholder="e.g. 10000"
                  class="custom-input modern-input with-prefix"
                />
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Bills Included?</label>
              <div class="toggle-group">
                <button type="button" :class="{'toggle-btn': true, 'active': billsIncluded === true}" @click="billsIncluded = true">Yes</button>
                <button type="button" :class="{'toggle-btn': true, 'active': billsIncluded === false}" @click="billsIncluded = false">No</button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title"><span class="section-icon">🛏️</span> Room Setup</h3>
          
          <div class="input-group">
            <div class="input-field">
              <label class="form-label">Shared Bathroom?</label>
              <div class="toggle-group">
                <button type="button" :class="{'toggle-btn': true, 'active': sharedBathroom === true}" @click="sharedBathroom = true">Yes</button>
                <button type="button" :class="{'toggle-btn': true, 'active': sharedBathroom === false}" @click="sharedBathroom = false">No</button>
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Bed Type</label>
              <div class="button-container">
                <button
                  v-for="(item, index) in buttons"
                  :key="index"
                  type="button"
                  :class="{'active': selectedButton === item, 'modern-btn': true}"
                  @click="selectButton(item)"
                  class="bed-type-button"
                >
                  <span class="btn-icon">🛏️</span> {{ item }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="tmp_rate">
      <div class="rateBlock">
        <h3 class="section-title"><span class="section-icon">⭐</span> Rate Your Experience</h3>
        
        <div class="rating-grid">
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248366345" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2664" width="24" height="24">
                  <path d="M945.152 174.592c0-19.456-15.872-35.328-35.328-35.84L870.4 138.24c-2.56 0-83.968-2.56-166.912-36.352-87.552-35.84-144.384-76.288-146.432-77.312l-23.552-17.408c-12.8-9.216-29.696-9.216-41.984 0l-23.552 16.896c-0.512 0.512-58.368 41.984-146.432 77.824C238.592 135.68 156.672 138.24 154.624 138.24h-40.448c-19.456 0.512-35.328 16.384-35.328 35.84v400.384c0 100.352 57.344 212.48 157.696 308.224C327.68 969.728 433.152 1024 512 1024c78.848 0 184.32-54.272 275.456-141.312 100.352-95.744 157.696-207.872 157.696-308.224V174.592z m-207.36 656.384C662.016 903.168 570.88 952.32 512 952.32s-150.016-48.64-225.792-121.344c-86.016-81.92-135.168-175.616-135.168-256V209.92h4.096c8.192 0 99.328-3.584 193.024-41.472C443.904 129.024 506.88 83.968 509.44 81.92l2.56-2.048 2.56 2.048c7.68 5.632 68.608 48.128 161.792 86.016 93.696 37.888 184.832 41.472 193.024 41.472h4.096v365.056c0 80.896-49.664 174.08-135.68 256.512z" fill="#667eea" p-id="2665"></path>
                  <path d="M669.184 352.256l-204.8 204.8L377.856 471.04c-17.92-17.92-46.592-17.92-64.512 0s-17.92 46.592 0 64.512l118.272 118.272c8.704 8.704 19.968 13.312 32.256 13.312s23.552-4.608 32.256-13.312l237.056-237.056c17.92-17.92 17.92-46.592 0-64.512-17.408-17.92-46.08-17.92-64 0z" fill="#667eea" p-id="2666"></path>
                </svg>
              </div>
              <span class="rating-label">Safety</span>
            </div>
            <div class="rating-hint">How safe did you feel living in this property and surrounding area?</div>
            <el-rate
              v-model="safety"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248572206" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="3867" width="24" height="24">
                  <path d="M854.6 289.1c-18.8-43.4-45.7-82.3-79.9-115.7-34.2-33.4-73.9-59.5-118.2-77.8C610.7 76.6 562.1 67 512 67c-50.1 0-98.7 9.6-144.5 28.5-44.3 18.3-84 44.5-118.2 77.8-34.2 33.4-61.1 72.4-79.9 115.7-19.5 45-29.4 92.8-29.4 142 0 70.6 16.9 140.9 50.1 208.7 26.7 54.5 64 107.6 111 158.1 80.3 86.2 164.5 138.9 188.4 153 6.9 4.1 14.7 6.1 22.4 6.1 7.8 0 15.5-2 22.4-6.1 23.9-14.1 108.1-66.8 188.4-153 47-50.4 84.3-103.6 111-158.1C867.1 572 884 501.8 884 431.1c0-49.2-9.9-97-29.4-142zM512 880.2c-65.9-41.9-300-207.8-300-449.1 0-77.9 31.1-151.1 87.6-206.3C356.3 169.5 431.7 139 512 139s155.7 30.5 212.4 85.9C780.9 280 812 353.2 812 431.1c0 241.3-234.1 407.2-300 449.1z" p-id="3868" fill="#667eea"></path>
                  <path d="M512 263c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176z m79.2 255.2C570 539.3 541.9 551 512 551c-29.9 0-58-11.7-79.2-32.8C411.7 497 400 468.9 400 439c0-29.9 11.7-58 32.8-79.2C454 338.6 482.1 327 512 327c29.9 0 58 11.6 79.2 32.8C612.4 381 624 409.1 624 439c0 29.9-11.6 58-32.8 79.2z" p-id="3869" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Location Convenience</span>
            </div>
            <div class="rating-hint">How convenient is this location for daily student life (campus, transport, food)?</div>
            <el-rate
              v-model="location"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg x="1741248732697" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="13377" width="24" height="24">
                  <path d="M580 888h296a20 20 0 0 1 20 20 20 20 0 0 1-20 20h-296a20 20 0 0 1-20-20 20 20 0 0 1 20-20zM493.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L531.84 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.08-24.88zM341.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L379.84 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.08-24.88zM417.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L456 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.24-24.88z" fill="#667eea" p-id="13378"></path>
                  <path d="M569.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L608 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.24-24.88z" fill="#667eea" p-id="13379"></path>
                  <path d="M684.32 849.52L655.36 760H324.72l-39.36 92.24a25.84 25.84 0 0 0-1.92 9.44 24 24 0 0 0 24 24h397.28a104 104 0 0 1-20.4-36.16zM298.32 720h386.08l38 117.2a64 64 0 0 0 20.88 30.32l37.12 29.68a16 16 0 0 1-10 28.48H307.44a64 64 0 0 1-64-64 63.36 63.36 0 0 1 5.2-25.12z" fill="#667eea" p-id="13380"></path>
                  <path d="M228 720h512a20 20 0 0 1 20 20 20 20 0 0 1-20 20h-512a20 20 0 0 1-20-20 20 20 0 0 1 20-20z" fill="#667eea" p-id="13381"></path>
                  <path d="M376 720h216v-16a40 40 0 0 0-40-40H416a40 40 0 0 0-40 40z m40-96h136a80 80 0 0 1 80 80v56H336v-56a80 80 0 0 1 80-80z" fill="#667eea" p-id="13382"></path>
                  <path d="M526.64 624l84.32-460.48a24.4 24.4 0 0 0 0-4.16A23.36 23.36 0 0 0 588 136h-9.28a40 40 0 0 0-39.36 32.8L456 624z m52.08-528h9.28a63.44 63.44 0 0 1 63.36 63.36 60 60 0 0 1-1.04 11.36L560 664H408l92-502.4a80 80 0 0 1 78.72-65.6z" fill="#667eea" p-id="13383"></path>
                  <path d="M250.48 397.04l8 20.56a32 32 0 0 0 17.76 17.76l20.56 8a8 8 0 0 1 4.48 10.4 8 8 0 0 1-4.48 4.48l-20.56 8a32 32 0 0 0-17.76 17.76l-8 20.56a8 8 0 0 1-14.88 0l-8-20.56a32 32 0 0 0-17.76-17.76l-20.56-8a8 8 0 0 1-4.48-10.4 8 8 0 0 1 4.48-4.48l20.56-8a32 32 0 0 0 17.76-17.76l8-20.56a8 8 0 0 1 10.4-4.48 8 8 0 0 1 4.48 4.48zM837.6 597.04l9.12 22.8a32 32 0 0 0 17.84 17.76l22.8 9.12a8 8 0 0 1 4.4 10.4 8 8 0 0 1-4.4 4.48l-22.8 9.12a32 32 0 0 0-17.84 17.84l-9.12 22.8a8 8 0 0 1-10.4 4.4 8 8 0 0 1-4.48-4.4l-9.12-22.8a32 32 0 0 0-17.76-17.84l-22.8-9.12a8 8 0 0 1-4.48-10.4 8 8 0 0 1 4.48-4.48l22.8-9.12a32 32 0 0 0 17.76-17.76l9.12-22.8a8 8 0 0 1 10.4-4.48 8 8 0 0 1 4.48 4.48z" fill="#667eea" p-id="13384"></path>
                </svg>
              </div>
              <span class="rating-label">Cleanliness & Condition</span>
            </div>
            <div class="rating-hint">How clean and well-maintained was the property?</div>
            <el-rate
              v-model="cleanliness"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248869926" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="24003" width="24" height="24">
                  <path d="M409.6 336.756364l4.887273 47.941818 76.8 76.567273-33.978182-329.076364a34.210909 34.210909 0 0 0-67.258182-4.654546l-46.545454 186.181819L401.221818 372.363636zM274.850909 601.134545H64a34.210909 34.210909 0 0 0 0 68.421819H302.545455a34.210909 34.210909 0 0 0 33.28-26.065455l23.272727-101.003636L302.545455 485.934545zM933.469091 560.872727A60.509091 60.509091 0 0 0 884.363636 587.403636h-162.909091l-30.254545-166.865454a34.210909 34.210909 0 0 0-65.861818-5.585455l-46.545455 134.050909 53.76 53.76 13.498182-39.098181 10.705455 63.301818 27.927272 27.927272a43.52 43.52 0 0 0 5.818182 0H884.363636a60.741818 60.741818 0 1 0 50.036364-94.952727zM518.283636 723.316364L515.723636 698.181818l-76.8-76.8L465.454545 884.363636a34.210909 34.210909 0 0 0 30.021819 30.487273h4.189091a33.978182 33.978182 0 0 0 32.116363-23.272727l45.381818-130.792727-53.76-53.76zM174.545455 202.705455a34.210909 34.210909 0 0 0-48.407273 48.407272l661.410909 661.410909a34.210909 34.210909 0 0 0 23.272727 10.007273 34.210909 34.210909 0 0 0 23.272727-58.414545z" fill="#667eea" p-id="24004"></path>
                </svg>
              </div>
              <span class="rating-label">Noise Exposure</span>
            </div>
            <div class="rating-hint">How disruptive was noise during your stay?</div>
            <el-rate
              v-model="noiseLevel"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248911883" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="26234" width="24" height="24">
                  <path d="M168 88l-18.016 16.992-44.992 44.992-16.992 18.016 12.992 20.992 67.008 112 8.992 16H271.04l128 127.008c-114.336 114.912-259.84 260.8-264.992 266.016-50.144 50.112-50.304 132.288 0.96 180 50.08 49.376 131.456 50.624 179.04 0l0.96-1.024L512 691.008l198.016 198.976 2.976 2.016c50.208 47.392 130.112 47.936 177.024-2.016v-0.96h0.96c49.024-50.112 49.536-131.584-0.96-179.04l-1.024-0.96-166.976-166.016c112.32-10.464 200.32-105.376 201.984-220h0.992c0.064-0.672-0.064-1.344 0-2.016V320c3.104-37.024-4.832-72.32-24-103.008L879.04 183.04l-28 28-121.024 120.992-44.992-47.008 118.016-117.984 33.984-34.016-44-17.984A224.096 224.096 0 0 0 704 96c-123.04 0-224 100.96-224 224 0 13.408 2.848 25.056 4.992 36.992-14.016 14.08-24.544 25.504-40.992 42.016L316.992 272V176.992l-16-8.992-112-67.008-20.992-12.992zM704 160c4.544 0 7.68 2.624 12 3.008l-99.008 98.976-21.984 22.016 21.984 23.008 90.016 92.992 21.984 23.008 23.008-23.008 104-104c0.672 7.36 5.824 12.736 4.992 20.992V320c0 88.16-71.84 160-160 160-11.712 0-25.984-2.816-41.984-6.016l-17.024-2.976-12 12-360.96 361.984h-1.024v1.024c-22.784 24.864-62.528 25.44-88.992-1.024v-0.992h-1.024c-24.864-22.784-25.44-62.528 1.024-88.992 12.096-12.128 247.008-249.024 361.984-364l13.024-12.992-5.024-18.016A198.08 198.08 0 0 1 544 320c0-88.16 71.84-160 160-160z m-526.016 8l75.008 46.016v32l-6.976 6.976h-32L168 178.016l10.016-10.016z m468 388.992l199.04 198.016v0.992h0.96c24.896 22.784 25.472 62.528-0.96 88.992h-1.024v1.024c-22.784 24.864-62.528 25.44-88.992-1.024l-198.016-198.976 89.024-89.024z" p-id="26235" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Maintenance Responsiveness</span>
            </div>
            <div class="rating-hint">How quickly and effectively were issues fixed?</div>
            <el-rate
              v-model="maintenance"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Listing Accuracy</span>
            </div>
            <div class="rating-hint">Did the property match the listing photos and description?</div>
            <el-rate
              v-model="utilities"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Internet & Utilities Reliability</span>
            </div>
            <div class="rating-hint">How reliable were internet, water, and electricity?</div>
            <el-rate
              v-model="internet"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Cost Transparency</span>
            </div>
            <div class="rating-hint">Were all costs clearly explained without hidden fees?</div>
            <el-rate
              v-model="rentValue"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Space Adequacy</span>
            </div>
            <div class="rating-hint">Was the room size suitable for the number of occupants?</div>
            <el-rate
              v-model="roomSize"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#667eea"></path>
                </svg>
              </div>
              <span class="rating-label">Living Comfort</span>
            </div>
            <div class="rating-hint">How comfortable was the overall living experience (privacy, shared spaces)?</div>
            <el-rate
              v-model="roomMates"
              :colors="colors"
              class="el_rate_yes"
            />
          </div>

        </div>
      </div>
    </div>

    <div class="reviewBlock">
      <div class="newb">
        <h2 class="review-title"><span class="review-icon">📝</span> Write a Review</h2>
        <p class="review-description">Tell us about your experience with this student housing. What's working well? What could be improved?</p>
        
        <div class="upload-section">
          <h3 class="upload-title"><span class="upload-icon">📷</span> Upload photos of your real experience in this property</h3>
          <div class="upload-container">
            <div class="file-input-wrapper" :class="{ 'is-loading': isCompressingImage }">
              <input type="file" @change="handleFileUpload2" placeholder="Choose File" class="file-input" accept="image/*" :disabled="isCompressingImage" />
              <div class="file-input-overlay">
                <template v-if="isCompressingImage">
                  <div class="compress-spinner"></div>
                  <span class="upload-text">Compressing...</span>
                </template>
                <template v-else>
                <span class="upload-icon">📷</span>
                <span class="upload-text">Choose Images</span>
                </template>
              </div>
            </div>

            <div class="image-gallery" v-if="images.length > 0">
              <div
                v-for="(image, index) in images"
                :key="index"
                class="image-preview"
              >
                <img :src="image" alt="Uploaded Image" />
                <button @click="removeImage2(index)" class="delete-button" :disabled="isCompressingImage">
                  <span>×</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="review-textarea-container" style="position:relative;">
          <textarea 
            v-model="reviewText" 
            placeholder="What do you want other students to know about this housing?" 
            maxlength="350"
            class="review-textarea"
          ></textarea>
          <div class="character-count">{{ reviewText.length }}/350</div>
        </div>

        <!-- Insight Fields -->
        <div class="insight-section">
          <h3 class="section-title"><span class="section-icon">💡</span> Quick Insights</h3>
          <div class="input-group">
            <div class="input-field" style="flex: 1 1 100%; position:relative;">
              <label class="form-label">What was the biggest problem?</label>
              <textarea v-model="biggestProblem" placeholder="e.g. Mould in bathroom, noisy neighbours, slow maintenance..." maxlength="200" class="review-textarea" style="min-height: 60px; height: 60px;"></textarea>
              <div class="character-count">{{ (biggestProblem || '').length }}/200</div>
            </div>
            <div class="input-field">
              <label class="form-label">Would you recommend this place?</label>
              <div class="toggle-group">
                <button type="button" :class="{'toggle-btn': true, 'active': recommendation === true}" @click="recommendation = true">👍 Yes</button>
                <button type="button" :class="{'toggle-btn': true, 'active': recommendation === false}" @click="recommendation = false">👎 No</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tag Selection -->
        <div class="insight-section">
          <h3 class="section-title"><span class="section-icon">🏷️</span> Tags (select all that apply)</h3>
          <div class="tag-grid">
            <button type="button" v-for="tag in availableTags" :key="tag" :class="{'tag-chip': true, 'active': selectedTags.includes(tag)}" @click="toggleTag(tag)">{{ tag }}</button>
          </div>
        </div>

        <!-- Submission Consent -->
        <div class="insight-section review-consent-section">
          <h3 class="section-title"><span class="section-icon">✅</span> {{ $t('pages.rateTheHouse.before_you_submit_title_7f3a1c2') }}</h3>

          <div class="review-consent-group">
            <label class="review-consent-checkbox">
              <input type="checkbox" v-model="consentHonest" />
              <span>{{ $t('pages.rateTheHouse.consent_honest_label_4d8e2b1') }}</span>
            </label>
            <label class="review-consent-checkbox">
              <input type="checkbox" v-model="consentIdentity" />
              <span>{{ $t('pages.rateTheHouse.consent_identity_label_9c1f6a3') }}</span>
            </label>
            <label class="review-consent-checkbox">
              <input type="checkbox" v-model="consentProviderShare" />
              <span>{{ $t('pages.rateTheHouse.consent_provider_share_label_2b7d5e4') }}</span>
            </label>
          </div>

          <div class="review-consent-group" v-if="images.length > 0">
            <label class="review-consent-checkbox">
              <input type="checkbox" v-model="consentPhotoRights" />
              <span>{{ $t('pages.rateTheHouse.consent_photo_rights_prefix_6a4f9d0') }}<a :href="termsOfUseHref" target="_blank" rel="noopener" class="review-consent-link">{{ $t('pages.rateTheHouse.consent_photo_rights_link_3e8c1b5') }}</a>{{ $t('pages.rateTheHouse.consent_photo_rights_suffix_1d9a7c6') }}</span>
            </label>
            <label class="review-consent-checkbox">
              <input type="checkbox" v-model="consentPhotoNoPrivate" />
              <span>{{ $t('pages.rateTheHouse.consent_photo_no_private_label_8f2b3a1') }}</span>
            </label>
          </div>
        </div>




        <div class="review-actions-row">
          <button
            type="button"
            class="draft-save-button"
            @click="saveDraftReview"
            :disabled="isSubmittingReview || isCompressingImage"
          >
            Save draft
          </button>

          <button class="submit-button" @click="submitReview" :disabled="isSubmittingReview || isCompressingImage || !reviewConsentComplete">
            <template v-if="isSubmittingReview">
              <div class="submit-spinner"></div>
              <span class="button-text">Submitting...</span>
            </template>
            <template v-else>
              <span class="button-text">Publish review</span>
              <span class="button-icon">→</span>
            </template>
          </button>
        </div>

        <p class="agreement-text">
          By clicking "Publish review", I confirm that I have read and agree to Sparrow's
          <a :href="communityGuidelinesHref" target="_blank" rel="noopener" class="agreement-link">Community Guidelines</a>,
          <a :href="termsOfUseHref" target="_blank" rel="noopener" class="agreement-link">Terms of Use</a>, and
          <a :href="privacyPolicyHref" target="_blank" rel="noopener" class="agreement-link">Privacy Policy</a>.
          Submitted reviews may be used to enhance the student housing experience.
        </p>
      </div>
    </div>
  </div>
  
  <!-- Add Room Modal -->
  <el-dialog
    :visible.sync="showAddRoomModal"
    title="Add a new room"
    width="520px"
    append-to-body
    custom-class="add-room-dialog"
    @close="closeAddRoomModal"
  >
    <div class="add-room-fields">
      <!-- Property Type -->
      <div class="add-room-field-group">
        <label class="add-room-label">Property Type</label>
        <el-select
          v-model="addRoomForm.propertyType"
          class="add-room-field"
          placeholder="Select property type"
          @change="onAddRoomPropertyTypeChange"
        >
          <el-option label="Village House" value="Village House" />
          <el-option label="Estate" value="Estate" />
          <el-option label="Apartment/Building" value="Apartment/Building" />
          <el-option label="Dorm" value="Dorm" />
          <el-option label="Studio" value="Studio" />
          <el-option label="Other" value="Other" />
        </el-select>
      </div>

      <!-- Detail fields once a property type is chosen (match el-select value, not only normalized type) -->
      <div v-if="addRoomShowDetailFields" class="add-room-detail-fields">
        <div v-if="addRoomShowBlock" class="add-room-field-group">
          <label class="add-room-label">Block</label>
          <el-input
            v-model.trim="addRoomForm.block"
            class="add-room-field"
            placeholder="Block (e.g., Block 8)"
            maxlength="25"
          />
        </div>

        <div class="add-room-field-group">
          <label class="add-room-label">Floor</label>
          <el-select
            v-model="addRoomForm.floor"
            class="add-room-field"
            placeholder="Choose or type a floor (e.g. G/F, 3/F)"
            filterable
            allow-create
            default-first-option
          >
            <el-option
              v-for="floor in floorOptions"
              :key="'fl-' + floor + '-' + addRoomNormalizedType"
              :label="floor"
              :value="floor"
            />
          </el-select>
        </div>

        <div v-if="addRoomShowRoomLabel" class="add-room-field-group">
          <label class="add-room-label">{{ addRoomRoomSecondaryLabel }}</label>
          <el-input
            v-model.trim="addRoomForm.roomLabel"
            class="add-room-field"
            :placeholder="addRoomRoomSecondaryPlaceholder"
            maxlength="25"
          />
        </div>

        <div v-if="addRoomShowUnit" class="add-room-field-group">
          <label class="add-room-label">Unit/Flat</label>
          <el-input
            v-model.trim="addRoomForm.unit"
            class="add-room-field"
            placeholder="Unit/Flat (e.g., Flat C)"
            maxlength="25"
          />
        </div>
      </div>

      <p v-if="addRoomError" class="add-room-error">
        {{ addRoomError }}
      </p>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="closeAddRoomModal">Cancel</el-button>
        <el-button
          type="primary"
          :loading="isCreatingRoom"
          @click="confirmAddRoom"
        >
          Continue
        </el-button>
      </span>
    </template>
  </el-dialog>
</div>
</template>
<style scoped>
.shared-bathroom-container {
  padding: 10px;
  margin-top: 25px;
}

.checkbox-label {
  margin-top: 10px;
  display: flex;
  align-items: center;
  color: #333;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

.selected-value {
  margin-top: 10px;
  color: #666;
  font-size: 0.9rem;
}

.draft-hint {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
}

.linkish-btn {
  display: inline;
  margin-left: 6px;
  padding: 0;
  border: none;
  background: none;
  color: #3b82f6;
  font-size: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.linkish-btn:hover {
  color: #2563eb;
}
</style>
<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import imageCompression from 'browser-image-compression';

const REVIEW_DRAFT_PREFIX = 'sparrow_rate_draft_v1';
/** Persisted while editing; per house + room. */
const REVIEW_DRAFT_KEYS = [
  'sharedBathroom',
  'safety',
  'location',
  'cleanliness',
  'noiseLevel',
  'maintenance',
  'utilities',
  'internet',
  'rentValue',
  'roomMates',
  'roomSize',
  'roomType',
  'stayDuration',
  'roommatesNum',
  'monthlyRent',
  'deposit',
  'billsIncluded',
  'selectedButton',
  'reviewText',
  'biggestProblem',
  'recommendation',
  'selectedTags',
  'filesUrl',
];

export default{
  data()  {
return{
  sharedBathroom: null,
  iconClasses: ['icon-rate-face-1', 'icon-rate-face-2', 'icon-rate-face-3'],
  colors: ['#f87171', '#facc15', '#34d399'],  // red → amber → green for clear visual feedback
  houseId:'',
  houseLetId:'',
  images: [],
  fileschos: [], // 用于存储上传的文件对象
  isCompressingImage: false, // 图片压缩中状态
  isSubmittingReview: false, // 提交评论中状态
  saveAsDraftSubmit: false,
  buttons: ['Bunk', '1 Bed', '2+ Beds'],
  selectedButton: null, // 初始化时没有按钮被选中
  file: null,
      fileName: '',
      fileContent: '',
        safety: 0,
        location: 0,
        cleanliness: 0,
        noiseLevel: 0,
        maintenance: 0,
        utilities: 0,
        internet: 0,
        rentValue: 0,
        roomMates: 0,
        roommatesNum: 0,
  roomSize:0,
        reviewText: '',
  roomType: '',
  stayDuration: '',
  monthlyRent: null,
  deposit: null,
  billsIncluded: null,
  biggestProblem: '',
  recommendation: null,
  selectedTags: [],
  // Submission consent: always defaults to false and is never restored from a
  // saved draft, so ticking is a fresh affirmative action on every submission.
  consentHonest: false,
  consentIdentity: false,
  consentProviderShare: false,
  consentPhotoRights: false,
  consentPhotoNoPrivate: false,
  availableTags: [
    'Nearby university', 'Far from university', 'Clean', 'Dirty',
    'Quiet', 'Noisy', 'Safe', 'Unsafe',
    'Spacious', 'Cramped', 'Worth the price', 'Not worth the price',
    'Accurate listing', 'Misleading photos', 'Transparent pricing', 'Hidden fees',
    'Well maintained', 'Poor maintenance', 'Convenient location', 'Inconvenient location'
  ],
      imageUploadId: 'image-upload',
      house:{},
      houselet:{},
      filesUrl: '',
      ifFirst: false,
    roomOptions: [],
    roomsLoading: false,
    selectedRoomId: null,
    showAddRoomModal: false,
    isCreatingRoom: false,
    addRoomForm: {
      propertyType: '',
      floor: '',
      roomLabel: '',
      block: '',
      unit: ''
    },
    addRoomError: '',
    floorOptions: ['G/F', 'LG/F', '1/F', '2/F', '3/F', '4/F', '5/F', '6/F', '7/F', '8/F', '9/F', '10/F'],
    draftRestoring: false,
    draftSaveTimer: null,
    /** True until initial / room-switch restore finishes so we don't overwrite the draft. */
    suppressDraftSave: true,


};
  },
  computed: {
    displayRoomLabel() {
      const h = this.houselet;
      if (!h || typeof h !== 'object') return '';
      const n = (h.houseLetName || h.houseletname || '').trim();
      return n || '';
    },
    reviewDraftWire() {
      const o = {};
      REVIEW_DRAFT_KEYS.forEach((k) => {
        o[k] = this[k];
      });
      return JSON.stringify(o);
    },
    addRoomNormalizedType() {
      return this.normalizeAddRoomPropertyType(this.addRoomForm.propertyType);
    },
    addRoomShowDetailFields() {
      const raw = this.addRoomForm.propertyType;
      return raw != null && String(raw).trim() !== '';
    },
    addRoomShowBlock() {
      return this.addRoomNormalizedType === 'Estate';
    },
    addRoomShowRoomLabel() {
      return ['Village House', 'Dorm', 'Studio', 'Other'].includes(this.addRoomNormalizedType);
    },
    addRoomShowUnit() {
      return this.addRoomNormalizedType === 'Estate' || this.addRoomNormalizedType === 'Apartment/Building';
    },
    addRoomRoomSecondaryLabel() {
      if (this.addRoomNormalizedType === 'Studio') return 'Unit / studio';
      return 'Room';
    },
    addRoomRoomSecondaryPlaceholder() {
      if (this.addRoomNormalizedType === 'Dorm') return 'Room or hall (e.g., Room 302, Hall A)';
      if (this.addRoomNormalizedType === 'Studio') return 'Unit or studio ID (e.g., S12)';
      return 'Room (e.g., Room A)';
    },
    // Gate for the final-submission button: the three review consent boxes are
    // always required, and the two photo consent boxes are additionally
    // required only when photos are attached (see submitReview() for the
    // second line of defence in case this is bypassed).
    reviewConsentComplete() {
      if (!(this.consentHonest && this.consentIdentity && this.consentProviderShare)) {
        return false;
      }
      if (this.images.length > 0) {
        return this.consentPhotoRights && this.consentPhotoNoPrivate;
      }
      return true;
    },
    // Resolved via the router so these always match the registered routes
    // (see router/index.js) and open the standalone legal pages in a new tab.
    termsOfUseHref() {
      return this.$router.resolve({ path: '/terms-of-use' }).href;
    },
    privacyPolicyHref() {
      return this.$router.resolve({ path: '/privacy-policy' }).href;
    },
    communityGuidelinesHref() {
      return this.$router.resolve({ path: '/terms-of-use', hash: '#appendix-b' }).href;
    },
  },
  watch: {
    reviewDraftWire() {
      this.scheduleDraftSave();
    },
  },
  mounted(){
    
  },
  created() {
    
    // houseId: resp.houseId,houseLetId: resp.houseLetId 
        // 从路由查询参数中获取搜索关键词
        const houseId = this.$route.query.houseId;
        const houseLetId = this.$route.query.houseLetId;
        const iff =  this.$route.query.ifFirst;
        this.houseId = houseId;
        this.houseLetId = houseLetId;
        this.ifFirst = (iff === true || iff === 'true')
    this.selectedRoomId = houseLetId ? Number(houseLetId) : null;

        console.log(this.ifFirst)
        console.log("111")

        this.fetchHouseInfo(houseId);
        this.fetchHouseLetInfo(houseLetId);
    this.fetchRoomOptions(houseId);
        this.$nextTick(() => {
          this.restoreReviewDraft();
          this.$nextTick(() => {
            this.suppressDraftSave = false;
          });
        });
    },
    methods:{
    reviewDraftStorageKey(houseId, houseLetId) {
      return `${REVIEW_DRAFT_PREFIX}:${houseId}:${houseLetId}`;
    },
    scheduleDraftSave() {
      if (
        !this.houseId ||
        !this.houseLetId ||
        this.draftRestoring ||
        this.suppressDraftSave ||
        this.isSubmittingReview
      ) {
        return;
      }
      clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = setTimeout(() => this.saveReviewDraft(), 600);
    },
    saveReviewDraft() {
      if (!this.houseId || !this.houseLetId || this.draftRestoring) return;
      const payload = {};
      REVIEW_DRAFT_KEYS.forEach((k) => {
        payload[k] = this[k];
      });
      try {
        localStorage.setItem(
          this.reviewDraftStorageKey(this.houseId, this.houseLetId),
          JSON.stringify(payload)
        );
      } catch (e) {
        console.warn('Could not save review draft', e);
      }
    },
    restoreReviewDraft() {
      // Consent is never part of the persisted draft (see REVIEW_DRAFT_KEYS)
      // and must always be a fresh affirmative action, so it is cleared here
      // regardless of whether a draft is actually found below.
      this.consentHonest = false;
      this.consentIdentity = false;
      this.consentProviderShare = false;
      this.consentPhotoRights = false;
      this.consentPhotoNoPrivate = false;

      const hid = this.houseId;
      const lid = this.houseLetId;
      if (!hid || !lid) return;
      let raw;
      try {
        raw = localStorage.getItem(this.reviewDraftStorageKey(hid, lid));
      } catch (e) {
        return;
      }
      if (!raw) return;
      let d;
      try {
        d = JSON.parse(raw);
      } catch (e) {
        return;
      }
      this.draftRestoring = true;
      let restored = false;
      REVIEW_DRAFT_KEYS.forEach((k) => {
        if (!Object.prototype.hasOwnProperty.call(d, k)) return;
        if (!(k in this)) return;
        if (k === 'selectedTags' && d[k] != null && !Array.isArray(d[k])) return;
        this[k] = d[k];
        restored = true;
      });
      this.draftRestoring = false;
      if (restored && this.$message) {
        this.$message({
          message: 'Restored your saved draft for this room.',
          type: 'info',
          duration: 3500,
        });
      }
    },
    clearReviewDraft() {
      if (!this.houseId || !this.houseLetId) return;
      try {
        localStorage.removeItem(this.reviewDraftStorageKey(this.houseId, this.houseLetId));
      } catch (e) {
        /* ignore */
      }
    },
    quickFillRequiredFields() {
      const ratings = ['safety', 'location', 'cleanliness', 'noiseLevel', 'maintenance', 'utilities', 'internet', 'rentValue', 'roomMates', 'roomSize'];
      ratings.forEach((key) => {
        if (this[key] === 0 || this[key] === null || this[key] === undefined) {
          this[key] = 3;
        }
      });
      if (!this.roomType) this.roomType = 'Private';
      if (!this.stayDuration) this.stayDuration = '2';
      if (this.monthlyRent === null || this.monthlyRent === undefined || this.monthlyRent === '') {
        this.monthlyRent = 0;
      }
      if (this.roommatesNum === null || this.roommatesNum === undefined || this.roommatesNum === '') {
        this.roommatesNum = 0;
      }
      if (!this.biggestProblem || !String(this.biggestProblem).trim()) {
        this.biggestProblem = 'None significant.';
      }
      if (this.recommendation === null || this.recommendation === undefined) {
        this.recommendation = true;
      }
      if (this.billsIncluded === null || this.billsIncluded === undefined) {
        this.billsIncluded = false;
      }
      if (this.sharedBathroom === null || this.sharedBathroom === undefined) {
        this.sharedBathroom = false;
      }
      if (!this.selectedButton) {
        this.selectedButton = '1 Bed';
      }
      const rt = (this.reviewText || '').trim();
      if (rt.length < 10) {
        this.reviewText = 'Short review — please edit before submitting.';
      }
      if (this.$message) {
        this.$message({
          message: 'Filled empty fields with neutral values. Adjust ratings and text as needed.',
          type: 'success',
          duration: 4000,
        });
      }
    },
    fetchRoomOptions(houseId) {
      if (!houseId) {
        this.roomOptions = [];
        return Promise.resolve();
      }
      this.roomsLoading = true;
      return axios
        .get('/api/search/room', {
          params: {
            houseId,
            pageNo: 1,
            pageSize: 100,
          },
          timeout: 10000,
        })
        .then((response) => {
          const raw = response.data?.data || [];
          this.roomOptions = this.normalizeRoomOptionsList(raw);
        })
        .catch((error) => {
          console.error('Failed to load rooms:', error);
          this.roomOptions = [];
        })
        .finally(() => {
          this.roomsLoading = false;
        });
    },
    roomOptionId(room) {
      if (!room || typeof room !== 'object') return null;
      const raw = room.id != null ? room.id : room.houseLetId != null ? room.houseLetId : room.houseLetID;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    roomOptionLabel(room) {
      if (!room || typeof room !== 'object') return 'Room';
      const id = this.roomOptionId(room);
      const label = String(room.houseLetName || room.houseletname || room.name || '').trim();
      if (label) return label;
      return id != null ? `Room ${id}` : 'Room';
    },
    normalizeRoomOptionsList(list) {
      if (!Array.isArray(list)) return [];
      const out = [];
      const seen = new Set();
      list.forEach((r) => {
        const id = this.roomOptionId(r);
        if (id == null || seen.has(id)) return;
        seen.add(id);
        out.push({
          ...r,
          id,
          houseLetId: id,
          houseLetName: this.roomOptionLabel({ ...r, id, houseLetId: id }),
        });
      });
      return out;
    },
    /** ES indexing can lag; keep the new room visible in the dropdown. */
    ensureRoomInOptions(roomId, label) {
      const id = Number(roomId);
      if (!Number.isFinite(id)) return;
      const list = this.normalizeRoomOptionsList(this.roomOptions || []);
      if (!list.some((r) => Number(r.id) === id)) {
        const text = (label && String(label).trim()) || `Room ${id}`;
        list.push({ id, houseLetId: id, houseLetName: text });
      }
      this.roomOptions = list;
    },
    handleRoomSwitch(value) {
      if (value === '__add_room__') {
        const prevId =
          this.houseLetId != null && this.houseLetId !== '' ? Number(this.houseLetId) : null;
        this.openAddRoomModal();
        this.$nextTick(() => {
          this.selectedRoomId = prevId;
        });
        return;
      }
      if (!value) {
        return;
      }
      this.selectedRoomId = value;
      this.houseLetId = value;
      this.suppressDraftSave = true;
      this.resetFormForRoomChange();
      this.fetchHouseLetInfo(value);
      this.$router.replace({
        path: this.$route.path,
        query: {
          houseId: this.houseId,
          houseLetId: value,
          ifFirst: this.ifFirst
        }
      });
      this.$nextTick(() => {
        this.restoreReviewDraft();
        this.$nextTick(() => {
          this.suppressDraftSave = false;
        });
      });
    },
    openAddRoomModal() {
      const fromLet = this.houselet && this.houselet.propertyType;
      const initial = fromLet ? this.normalizeAddRoomPropertyType(fromLet) : '';
      this.addRoomForm = {
        propertyType: initial,
        floor: '',
        roomLabel: '',
        block: '',
        unit: ''
      };
      this.addRoomError = '';
      this.showAddRoomModal = true;
    },
    normalizeAddRoomPropertyType(raw) {
      if (raw == null || raw === '') return '';
      const s = String(raw).trim();
      const known = new Set(['Village House', 'Estate', 'Apartment/Building', 'Dorm', 'Studio', 'Other']);
      if (known.has(s)) return s;
      const lower = s.toLowerCase();
      const map = {
        'village house': 'Village House',
        estate: 'Estate',
        'apartment/building': 'Apartment/Building',
        apartment: 'Apartment/Building',
        building: 'Apartment/Building',
        dorm: 'Dorm',
        dormitory: 'Dorm',
        studio: 'Studio',
        other: 'Other',
      };
      if (map[lower]) return map[lower];
      if (lower.includes('apartment') || (lower.includes('building') && !lower.includes('village'))) {
        return 'Apartment/Building';
      }
      if (lower.includes('village')) return 'Village House';
      if (lower.includes('estate')) return 'Estate';
      if (lower.includes('dorm')) return 'Dorm';
      if (lower.includes('studio')) return 'Studio';
      return 'Other';
    },
    onAddRoomPropertyTypeChange(val) {
      const n = this.normalizeAddRoomPropertyType(val);
      this.$set(this.addRoomForm, 'propertyType', n);
      this.addRoomError = '';
    },
    closeAddRoomModal() {
      this.showAddRoomModal = false;
      this.addRoomForm = {
        propertyType: '',
        floor: '',
        roomLabel: '',
        block: '',
        unit: ''
      };
      this.addRoomError = '';
      // Reset the select to previous value
      this.selectedRoomId = this.houseLetId ? Number(this.houseLetId) : null;
    },
    validateAddRoomForm() {
      const pt = this.normalizeAddRoomPropertyType(this.addRoomForm.propertyType);
      if (pt !== this.addRoomForm.propertyType) {
        this.addRoomForm.propertyType = pt;
      }
      if (!pt) {
        this.addRoomError = 'Please select a property type.';
        return false;
      }

      if (!this.addRoomForm.floor?.trim()) {
        this.addRoomError = 'Please choose a floor (pick from the list or type your own, e.g. G/F).';
        return false;
      }

      if (['Village House', 'Other', 'Dorm', 'Studio'].includes(pt)) {
        if (!this.addRoomForm.roomLabel?.trim()) {
          this.addRoomError = 'Please enter a room or unit label.';
          return false;
        }
      }

      if (pt === 'Estate') {
        if (!this.addRoomForm.block?.trim()) {
          this.addRoomError = 'Please enter a block.';
          return false;
        }
        if (!this.addRoomForm.unit?.trim()) {
          this.addRoomError = 'Please enter a unit/flat.';
          return false;
        }
      }

      if (pt === 'Apartment/Building') {
        if (!this.addRoomForm.unit?.trim()) {
          this.addRoomError = 'Please enter a unit/flat.';
          return false;
        }
      }

      this.addRoomError = '';
      return true;
    },
    async confirmAddRoom() {
      if (!this.validateAddRoomForm()) {
        return;
      }
      this.isCreatingRoom = true;
      try {
        const pt = this.normalizeAddRoomPropertyType(this.addRoomForm.propertyType);
        // Construct room name based on property type and fields
        let roomName = '';
        if (['Village House', 'Other', 'Dorm', 'Studio'].includes(pt)) {
          roomName = `${this.addRoomForm.floor.trim()} ${this.addRoomForm.roomLabel.trim()}`;
        } else if (pt === 'Estate') {
          roomName = `${this.addRoomForm.block.trim()} ${this.addRoomForm.floor.trim()} ${this.addRoomForm.unit.trim()}`;
        } else if (pt === 'Apartment/Building') {
          roomName = `${this.addRoomForm.floor.trim()} ${this.addRoomForm.unit.trim()}`;
        } else {
          roomName = `${this.addRoomForm.floor.trim()} ${(this.addRoomForm.roomLabel || this.addRoomForm.unit || '').trim()}`.trim();
        }
        
        // Resolve the canonical room identifier.
        // HouseDTO uses "room" for all unit/flat/room sub-identifiers; "unit" is not a DTO field.
        // roomLabel covers Village House / Dorm / Studio / Other.
        // For Estate and Apartment/Building the flat lives in addRoomForm.unit.
        const roomValue = ['Estate', 'Apartment/Building'].includes(pt)
          ? (this.addRoomForm.unit?.trim() || '')
          : (this.addRoomForm.roomLabel?.trim() || '');

        // Create the room via API (using the same endpoint as AddHouseRoom)
        const hi = this.house && typeof this.house === 'object' ? this.house : {};
        const response = await axios.post('/api/house/add', {
          "houseId": this.houseId,
          "houseLetId": 0,
          "name": hi.name || '',
          "type": hi.type || '',
          "region": hi.region || '',
          "district": hi.district || '',
          "area": hi.area || '',
          "detailedArea": hi.detailedArea || '',
          "street": hi.street || '',
          "address": hi.address || '',
          "houseLet": roomName,
          "propertyType": this.normalizeAddRoomPropertyType(this.addRoomForm.propertyType),
          "floor": this.addRoomForm.floor.trim(),
          "room": roomValue,
          "block": this.addRoomForm.block?.trim() || ''
        }, { timeout: 10000 });
        
        const pdata = response.data;
        if (!pdata || !pdata.data) {
          this.addRoomError = pdata?.msg || 'Unable to create room. Please try again.';
          return;
        }
        if (pdata.code != null && Number(pdata.code) !== 1) {
          this.addRoomError = pdata.msg || 'Unable to create room. Please try again.';
          return;
        }

        const resp = pdata.data;
        const newRoomId = resp.houseLetId || resp.id;
        if (!newRoomId) {
          this.addRoomError = 'Server did not return a room id. Please try again.';
          return;
        }
        const numericNewId = Number(newRoomId);

        // New unit must start with a clean review form — do not keep the previous room's
        // ratings, text, tags, or images (they read as "wrong" data for this room).
        this.suppressDraftSave = true;
        this.resetFormForRoomChange();

        this.houseLetId = numericNewId;
        this.selectedRoomId = numericNewId;

        await this.fetchRoomOptions(this.houseId);

        try {
          const letRes = await axios.get('/api/house/let', {
            params: { houseletId: numericNewId },
            timeout: 10000,
          });
          if (letRes.data && letRes.data.data) {
            this.houselet = letRes.data.data != null ? letRes.data.data : {};
          }
        } catch (e) {
          console.warn('Could not load house let details for new room', e);
        }

        const displayName =
          (this.houselet &&
            String(this.houselet.houseLetName || this.houselet.houseletname || '').trim()) ||
          roomName;
        this.ensureRoomInOptions(numericNewId, displayName);

        this.closeAddRoomModal();

        this.$router.replace({
          path: this.$route.path,
          query: {
            houseId: this.houseId,
            houseLetId: numericNewId,
            ifFirst: false,
          },
        });

        this.$nextTick(() => {
          this.selectedRoomId = numericNewId;
          this.houseLetId = numericNewId;
          this.restoreReviewDraft();
          this.$nextTick(() => {
            this.suppressDraftSave = false;
          });
        });

        if (this.$message) {
          this.$message.success('Room added. Your review form was cleared for this unit.');
        }
      } catch (error) {
        console.error('Failed to add room', error);
        const status = error?.response?.status;
        const body = error?.response?.data;
        if (status === 409 && body?.error === 'DUPLICATE_ROOM' && body?.existingHouseLetId) {
          // Room already exists — silently switch to it as if it were freshly created
          const existingId = Number(body.existingHouseLetId);
          this.suppressDraftSave = true;
          this.resetFormForRoomChange();
          this.houseLetId = existingId;
          this.selectedRoomId = existingId;
          await this.fetchRoomOptions(this.houseId);
          this.closeAddRoomModal();
          this.$router.replace({
            path: this.$route.path,
            query: { houseId: this.houseId, houseLetId: existingId, ifFirst: false },
          });
          this.$nextTick(() => {
            this.selectedRoomId = existingId;
            this.houseLetId = existingId;
            this.restoreReviewDraft();
            this.$nextTick(() => { this.suppressDraftSave = false; });
          });
          if (this.$message) {
            this.$message.info('This room already exists — switched to it for your review.');
          }
          return;
        }
        this.addRoomError = error?.response?.data?.msg || error?.response?.data?.message || 'Unable to create room. Please try again.';
      } finally {
        this.isCreatingRoom = false;
      }
    },
    resetFormForRoomChange() {
      this.roomType = '';
      this.stayDuration = '';
      this.monthlyRent = null;
      this.deposit = null;
      this.billsIncluded = null;
      this.roommatesNum = 0;
      this.sharedBathroom = null;
      this.selectedButton = null;
      this.safety = 0;
      this.location = 0;
      this.cleanliness = 0;
      this.noiseLevel = 0;
      this.maintenance = 0;
      this.utilities = 0;
      this.internet = 0;
      this.rentValue = 0;
      this.roomMates = 0;
      this.roomSize = 0;
      this.reviewText = '';
      this.biggestProblem = '';
      this.recommendation = null;
      this.selectedTags = [];
      this.images = [];
      this.fileschos = [];
      this.file = null;
      this.fileName = '';
      this.fileContent = '';
      this.filesUrl = '';
      // Consent is per-review, per-room: never carry a ticked box into a
      // different room's form.
      this.consentHonest = false;
      this.consentIdentity = false;
      this.consentProviderShare = false;
      this.consentPhotoRights = false;
      this.consentPhotoNoPrivate = false;
    },
    async handleFileUpload2(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      this.isCompressingImage = true;

      const compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg'
      };
      const MAX_FALLBACK_SIZE = 5 * 1024 * 1024;
      const skipped = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isHeic = /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type);
        if (isHeic) {
          // browser-image-compression decodes via canvas; Chrome / Firefox can't
          // read HEIC, so the upload silently fails. Reject up front.
          skipped.push(`${file.name} — HEIC/HEIF not supported, please re-export as JPEG`);
          continue;
        }

        let toUpload = file;
        if (file.size > 1024 * 1024) {
          try {
            toUpload = await imageCompression(file, compressionOptions);
          } catch (error) {
            console.error('Image compression failed for', file.name, error);
            if (file.size > MAX_FALLBACK_SIZE) {
              skipped.push(`${file.name} — too large to upload uncompressed`);
              continue;
            }
            toUpload = file;
          }
        }

        this.fileschos.push(toUpload);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.images.push(e.target.result);
        };
        reader.readAsDataURL(toUpload);
      }

      this.isCompressingImage = false;
      event.target.value = '';

      if (skipped.length) {
        Swal.fire({
          title: skipped.length === files.length ? 'Upload failed' : 'Some images skipped',
          text: skipped.join('\n'),
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3bc2ff'
        });
      }
    },
    handleFileUpload1(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        reader.onload = (e) => {
          this.images.push(e.target.result); // 将图片的 Base64 URL 添加到数组
        };
        reader.readAsDataURL(file); // 读取文件为 Base64 格式
      }
    },
    clearImages() {
      this.images = []; // 清空所有图片
    },
    removeImage(index) {
      this.images.splice(index, 1); // 删除指定索引的图片
            this.fileschos = this.fileschos.filter((_, i) => i !== index); // 同时删除对应的文件对象
        console.log(this.fileschos)
    },
      removeImage2(index) {
      this.images.splice(index, 1); // 删除图片的 Base64 URL
      this.fileschos.splice(index, 1); // 同时删除对应的文件对象
        console.log(this.fileschos)
    },
    handleFileUpload(event) {
      this.file = event.target.files[0];
    },
preupload1() {
  if (this.saveAsDraftSubmit) {
    if (this.roommatesNum < 0 || this.monthlyRent < 0 || this.deposit < 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid input',
        text: 'Negative numbers are not allowed.',
        confirmButtonColor: '#3bc2ff'
      });
      this.saveAsDraftSubmit = false;
      return;
    }
    const hasNewImages = Array.isArray(this.fileschos) && this.fileschos.length > 0;
    if (!hasNewImages) {
      this.rateHouse(this);
    } else {
      this.uploadFileNew(this);
    }
    return;
  }

  // Check for invalid values
  if (this.roommatesNum < 0 || this.monthlyRent < 0 || this.deposit < 0) {
    let negativeField = '';
    if (this.roommatesNum < 0) negativeField = 'Number of roommates';
    else if (this.monthlyRent < 0) negativeField = 'Monthly rent';
    else if (this.deposit < 0) negativeField = 'Deposit';
    Swal.fire({
      icon: 'error',
      title: 'Invalid input',
      text: `${negativeField} input is invalid`,
      confirmButtonColor: '#3bc2ff'
    });
    return;
  }

  // 检查是否为0
  const zeroFields = [];
  if (this.safety === 0) zeroFields.push('safety');
  if (this.location === 0) zeroFields.push('location');
  if (this.cleanliness === 0) zeroFields.push('cleanliness');
  if (this.noiseLevel === 0) zeroFields.push('noiseLevel');
  if (this.maintenance === 0) zeroFields.push('maintenance');
  if (this.utilities === 0) zeroFields.push('utilities');
  if (this.internet === 0) zeroFields.push('internet');
  if (this.rentValue === 0) zeroFields.push('rentValue');
  if (this.roomMates === 0) zeroFields.push('roomMates');
  if (this.roomSize === 0) zeroFields.push('roomSize');
  if (zeroFields.length > 0) {
    Swal.fire({
      icon: 'error',
      title: 'Incomplete rating',
      text: `Please rate the following: ${zeroFields.join(', ')}`,
      confirmButtonColor: '#3bc2ff'
    });
    return;
  }

  // 检查是否为空
  if (this.reviewText === '') {
    Swal.fire({
      icon: 'error',
      title: 'Missing review',
      text: 'Please write a review ~',
      confirmButtonColor: '#3bc2ff'
    });
    return;
  }

  // 原有逻辑
  console.log("filesURL");
  console.log(this.fileschos);
  const hasNewImages = Array.isArray(this.fileschos) && this.fileschos.length > 0;
  if (!hasNewImages) {
    this.rateHouse(this);
  } else {
    this.uploadFileNew(this);
  }
}
    
    ,
      preupload(){


        // if (this.checkForZeroFields()) {
        //         i
        //   }else{
        //     console.log('Form has fields with zero values. Please fill them out.');
        //   }

      
      },

      uploadFileNew(obj) {

      console.log(obj.fileschos)
        console.log(typeof obj.fileschos); // 输出类型
        console.log(typeof obj.fileschos[0]); // 输出类型
      // 没能append到'file'中
      // 创建 FormData 对象
      const formData = new FormData();

      // 遍历文件数组，将文件添加到 FormData 中
      this.fileschos.forEach((file) => {
        console.log(file)
        formData.append(`files`, file); // 可以为每个文件设置不同的 key，例如 file0, file1...
      });

      const formDataArray = Array.from(formData.entries());
      console.log(formDataArray);

      obj.isSubmittingReview = true;

      const expectedFiles = this.fileschos.length;

      axios.post('/api/upload/reviewimg', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      })
      .then(response => {
               console.log(response)
        let payload = response && response.data;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch (e) { payload = null; }
        }
        const code = payload && payload.code;
        const data = payload && payload.data;
        if (code !== undefined && Number(code) !== 1) {
          obj.isSubmittingReview = false;
          obj.saveAsDraftSubmit = false;
          Swal.fire({
            title: 'Upload failed',
            text: (payload && payload.msg) || 'Images were not saved. Fix the issue or try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
          return;
        }
        const urlStr = data != null ? String(data).trim() : '';
        if (!urlStr || urlStr.toLowerCase() === 'null') {
          obj.isSubmittingReview = false;
          obj.saveAsDraftSubmit = false;
          Swal.fire({
            title: 'Upload failed',
            text: 'The server returned no image URLs. Check MinIO or try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
          return;
        }
        const urlParts = urlStr.split(',').map(s => s.trim()).filter(Boolean);
        if (urlParts.length < expectedFiles) {
          Swal.fire({
            icon: 'warning',
            title: 'Some images may be missing',
            html: `Only <strong>${urlParts.length}</strong> of <strong>${expectedFiles}</strong> images were saved. You can continue and submit, or cancel and retry uploads.`,
            showCancelButton: true,
            confirmButtonText: 'Continue & submit review',
            cancelButtonText: 'Go back',
            confirmButtonColor: '#3bc2ff',
            cancelButtonColor: '#64748b'
          }).then((r) => {
            if (!r.isConfirmed) {
              obj.isSubmittingReview = false;
              obj.saveAsDraftSubmit = false;
              return;
            }
            obj.filesUrl = urlStr;
            obj.rateHouse(obj);
          });
          return;
        }
        obj.filesUrl = urlStr;
        console.log(obj.filesUrl)
        obj.rateHouse(obj);
      })
      .catch((err) => {
        obj.isSubmittingReview = false;
        obj.saveAsDraftSubmit = false;
        const msg = err?.response?.data?.msg || err?.message || 'Failed to upload images.';
        Swal.fire({
          title: 'Upload Failed',
          text: msg,
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3bc2ff'
        });
      });
    },
        checkForZeroFields() {
      const zeroFields = [];
      const fields = {
        safety: this.safety,
        location: this.location,
        cleanliness: this.cleanliness,
        noiseLevel: this.noiseLevel,
        maintenance: this.maintenance,
        utilities: this.utilities,
        internet: this.internet,
        rentValue: this.rentValue,
        roommates: this.roomMates,
        roomSize: this.roomSize,
      };

      for (const key in fields) {
        if (fields[key] === 0) {
          zeroFields.push(key);
        }
      }

      if (zeroFields.length > 0) {
        Swal.fire({
          title: 'Alert',
          text: `Please provide a rating for the following fields: ${zeroFields.join(', ')}`,
          icon: 'info',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3bc2ff'
        });
        return false;
      }
      return true;
    },
    uploadFile(obj) {


      console.log(obj.fileschos)
      // 没能append到'file'中
      // const formData = new FormData();
      // formData.append('file', obj.fileschos);
      const filesData = Array.from(this.fileschos).map(fileList => {
          return Array.from(fileList).map(file => ({
            name: file.name,
            lastModified: file.lastModified,
            lastModifiedDate: file.lastModifiedDate.toUTCString(),
            webkitRelativePath: file.webkitRelativePath,
            size: file.size
          }));
        });

        // 转换为 JSON 字符串
        const jsonData = JSON.stringify(filesData);
        console.log(jsonData);
      axios.post('/api/upload/reviewimg', jsonData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        console.log(response)
        alert('文件上传成功！保存路径：' + response.data.data);
        obj.filesUrl=response.data.data
        console.log(obj.filesUrl)
      })
      .catch(error => {
        alert('文件上传失败：' +error);
      });
    },

      uploadFiles() {
      const formData = new FormData();
      for (let i = 0; i < this.fileschos.length; i++) {
        formData.append('files', this.fileschos[i]); // 将每个文件追加到 FormData 中
      }

      console.log(formData)
      axios.post('/api/upload/reviewimg', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        alert('文件上传成功！保存路径：' + response.data); // 假设服务器返回的是保存路径
      })
      .catch(error => {
        alert('文件上传失败：' + error);
      });
    },
    // readFile() {
    //   if (!this.fileName) {
    //     alert('请输入文件名！');
    //     return;
    //   }
    //
    //   axios.get(`http://localhost:8088/upload/readFile?fileName=${this.fileName}`)
    //   .then(response => {
    //     this.fileContent = response.data;
    //   })
    //   .catch(error => {
    //     alert('读取文件失败：' + error.response.data);
    //   });
    // },

      fetchHouseInfo(houseId){
      this.getHouseInfo(this,houseId)
  },
  fetchHouseLetInfo(houseletId){
      this.getHouseLetInfo(this,houseletId)
  },

      getHouseInfo(obj,house){
        // 在这里实现跳转到房屋评分页面的逻辑

        axios.get('/api/house', {
          params: {
              houseId: house,
          },
          timeout: 10000
      })
      .then(function (response) {
          obj.house = response.data.data != null ? response.data.data : {};
          console.log(response.data.data);
      })
      .catch(function (error) {
          console.log(error);
          obj.house = {};
      });
  },
  getHouseLetInfo(obj,houseletId){
        // 在这里实现跳转到房屋评分页面的逻辑

        axios.get('/api/house/let', {
          params: {
            houseletId: houseletId,
          },
          timeout: 10000
      })
      .then(function (response) {
          obj.houselet = response.data.data != null ? response.data.data : {};
          obj.selectedRoomId = houseletId ? Number(houseletId) : null;
          console.log(response.data.data);
      })
      .catch(function (error) {
          console.log(error);
          obj.houselet = {};
      });
  },
      showGuidelines() {
      // 显示指南的逻辑
    },
    onFileChange(event) {
      // 处理文件上传的逻辑
      console.log(event.target.files[0]);
    },
    submitReview() {
      // Enhanced validation with detailed error messages
      const errors = [];
      
      // Check rating fields
      const ratingFields = [
        { name: 'Safety', value: this.safety },
        { name: 'Location Convenience', value: this.location },
        { name: 'Cleanliness & Condition', value: this.cleanliness },
        { name: 'Noise Exposure', value: this.noiseLevel },
        { name: 'Maintenance Responsiveness', value: this.maintenance },
        { name: 'Listing Accuracy', value: this.utilities },
        { name: 'Internet & Utilities Reliability', value: this.internet },
        { name: 'Cost Transparency', value: this.rentValue },
        { name: 'Space Adequacy', value: this.roomSize },
        { name: 'Living Comfort', value: this.roomMates }
      ];
      
      ratingFields.forEach(field => {
        if (field.value === 0) {
          errors.push(`${field.name} rating is required`);
        }
      });
      
      // Check review text
      if (!this.reviewText || this.reviewText.trim().length < 10) {
        errors.push('Review text must be at least 10 characters long');
      }
      
      // Check basic information fields
      if (!this.roomType) {
        errors.push('Please select your room type');
      }
      if (!this.stayDuration) {
        errors.push('Please select how long you stayed');
      }
      if (this.monthlyRent === null || this.monthlyRent === undefined || this.monthlyRent === '') {
        errors.push('Please enter the monthly rent');
      }
      if (this.roommatesNum === null || this.roommatesNum === undefined || this.roommatesNum === '' || this.roommatesNum < 0) {
        errors.push('Please enter the number of roommates (must be 0 or greater)');
      }
      if (!this.biggestProblem || this.biggestProblem.trim().length === 0) {
        errors.push('Please describe the biggest problem you faced');
      }
      if (this.recommendation === null || this.recommendation === undefined) {
        errors.push('Please indicate whether you would recommend this property');
      }
      if (this.billsIncluded === null || this.billsIncluded === undefined) {
        errors.push('Please indicate whether bills are included');
      }
      if (this.sharedBathroom === null || this.sharedBathroom === undefined) {
        errors.push('Please indicate whether the bathroom is shared');
      }

      // Second line of defence: the submit button is already disabled until
      // reviewConsentComplete is true, but guard here too in case that state
      // is ever bypassed (e.g. programmatic submit).
      if (!(this.consentHonest && this.consentIdentity && this.consentProviderShare)) {
        errors.push(this.$t('pages.rateTheHouse.consent_required_review_text_5c6d8e2'));
      }
      if (this.images.length > 0 && !(this.consentPhotoRights && this.consentPhotoNoPrivate)) {
        errors.push(this.$t('pages.rateTheHouse.consent_required_photo_text_9b3f1a4'));
      }

      // Show validation errors if any
      if (errors.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: '<span style="color: #2c3e50; font-size: 1.5rem; font-weight: 600;">Please Complete Required Fields</span>',
          html: `
            <div style="text-align: left; margin: 20px 0;">
              <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 15px; margin-bottom: 15px;">
                <div style="color: #856404; font-weight: 600; margin-bottom: 10px;">Required Information:</div>
                <div style="color: #856404;">
                  ${errors.map(error => `<div style="margin: 8px 0; padding-left: 20px; position: relative;">
                    <span style="position: absolute; left: 0; color: #e74c3c;">•</span>
                    ${error}
                  </div>`).join('')}
                </div>
              </div>
            </div>
          `,
          confirmButtonText: 'Got it!',
          confirmButtonColor: '#3bc2ff',
          cancelButtonColor: '#64748b',
          background: '#ffffff',
          showCancelButton: true,
          cancelButtonText: 'Close',
          allowOutsideClick: true,
          allowEscapeKey: true,
        });
        return;
      }
      
      // If validation passes, proceed with submission
      this.saveAsDraftSubmit = false;
      this.preupload1();
    },
    saveDraftReview() {
      if (!this.houseId || !this.houseLetId) {
        this.$message.error('Missing listing — cannot save draft.');
        return;
      }
      if (this.roommatesNum < 0 || this.monthlyRent < 0 || this.deposit < 0) {
        this.$message.error('Fix negative numbers before saving.');
        return;
      }
      this.saveAsDraftSubmit = true;
      this.preupload1();
    },
    toggleTag(tag) {
      const idx = this.selectedTags.indexOf(tag);
      if (idx === -1) {
        this.selectedTags.push(tag);
      } else {
        this.selectedTags.splice(idx, 1);
      }
    },
      selectButton(index) {
      // Toggle: clicking same button deselects, clicking different selects
      if (this.selectedButton === index) {
        this.selectedButton = null;
      } else {
        this.selectedButton = index;
      }
    },
        ToHouseReviewScore() {

          if (this.$route.path !== '/HouseReviewScore') {
            this.$router.push({ path: '/HouseReviewScore', query: { houseId: this.houseId,houseLetId: this.houseLetId } });
          }

        },
        ToAddLocation(){
          console.log(222)
          console.log(this.ifFirst)
          console.log(this.ifFirst===true)
          console.log(this.$route.path !== '/AddLocation' && this.ifFirst)
          if (this.$route.path !== '/AddLocation' && this.ifFirst) {
            this.$router.push({ path: '/AddLocation', query: { houseId: this.houseId,houseLetId: this.houseLetId } });
          }else{
            if (this.$route.path !== '/HouseReviewScore') {
              this.$router.push({ 
                path: '/HouseReviewScore', 
                query: { houseId: this.houseId, houseLetId: this.houseLetId } 
              });
            }
          }
        },
    rateHouse(obj) {
        const isDraft = !!obj.saveAsDraftSubmit;

        if (!this.houseId || !this.houseLetId) {
          this.$message.error('Missing house information. Please try again.');
          obj.saveAsDraftSubmit = false;
          return;
        }

        if (!isDraft) {
          if (!this.reviewText || this.reviewText.trim() === '') {
            this.$message.error('Please write a review before submitting.');
            return;
          }
          const ratings = [
            this.safety, this.location, this.cleanliness, this.noiseLevel,
            this.maintenance, this.utilities, this.internet, this.rentValue,
            this.roomMates, this.roomSize
          ];
          if (ratings.some((rating) => rating === 0 || rating === null || rating === undefined)) {
            this.$message.warning('Please provide all ratings before submitting.');
            return;
          }
        }

        console.log(isDraft ? 'Saving draft...' : 'Publishing review...');
        console.log('Files URL:', obj.filesUrl);

        const average = (
          parseFloat(this.safety) +
          parseFloat(this.location) +
          parseFloat(this.cleanliness) +
          parseFloat(this.noiseLevel) +
          parseFloat(this.maintenance) +
          parseFloat(this.utilities) +
          parseFloat(this.internet) +
          parseFloat(this.rentValue) +
          parseFloat(this.roomMates) +
          parseFloat(this.roomSize)
        ) / 10;

        const payload = {
          houseId: this.houseId,
          content: this.reviewText || '',
          safety: parseFloat(this.safety) || 0,
          location: parseFloat(this.location) || 0,
          cleanliness: parseFloat(this.cleanliness) || 0,
          noiseLevel: parseFloat(this.noiseLevel) || 0,
          maintenance: parseFloat(this.maintenance) || 0,
          listingAccuracy: parseFloat(this.utilities) || 0,
          internetReliability: parseFloat(this.internet) || 0,
          costTransparency: parseFloat(this.rentValue) || 0,
          livingComfort: parseFloat(this.roomMates) || 0,
          roomSize: parseFloat(this.roomSize) || 0,
          average: parseFloat(average.toFixed(5)),
          houseLetId: this.houseLetId,
          stayDuration: parseInt(this.stayDuration, 10) || 0,
          roommatesNum: parseInt(this.roommatesNum, 10) || 0,
          deposit: parseInt(this.deposit, 10) || 0,
          sharedBathroom: this.sharedBathroom ? 1 : 0,
          monthlyRent: parseInt(this.monthlyRent, 10) || 0,
          bedType: this.selectedButton || '',
          file: obj.filesUrl || '',
          recommend: this.recommendation,
          biggestProblem: this.biggestProblem || '',
          tags: this.selectedTags.join(','),
          billsIncluded: this.billsIncluded,
          roomType: this.roomType || '',
          ifWantVerify: 0,
          isShared: this.sharedBathroom ? 1 : 0,
          company: '',
          saveAsDraft: isDraft
        };

        console.log('Payload being sent:', payload);

        obj.isSubmittingReview = true;

        axios.post('/api/house/rate', payload, { timeout: 10000 })
        .then(function (response) {
          console.log('Success! Response:', response);
          obj.resp = response.data.data;
          obj.isSubmittingReview = false;
          obj.saveAsDraftSubmit = false;

          if (isDraft) {
            obj.$message.success('Draft saved. You can finish it anytime from Profile → My Reviews.');
            return;
          }

          obj.clearReviewDraft();

          // Consent must be re-affirmed for every new review; never carry a
          // ticked box past a successful submission.
          obj.consentHonest = false;
          obj.consentIdentity = false;
          obj.consentProviderShare = false;
          obj.consentPhotoRights = false;
          obj.consentPhotoNoPrivate = false;

          const photoStr = String(obj.filesUrl || '').trim();
          const photoCount = photoStr
            ? photoStr.split(',').map((s) => s.trim()).filter((s) => s && s.toLowerCase() !== 'null').length
            : 0;
          const msg =
            photoCount > 0
              ? `Review submitted successfully (${photoCount} photo${photoCount === 1 ? '' : 's'} saved).`
              : 'Review submitted successfully (no photos attached — add images if you meant to include them).';

          obj.$message.success(msg);

          obj.ToAddLocation(obj.resp);
        })
        .catch(function (error) {
          obj.isSubmittingReview = false;
          obj.saveAsDraftSubmit = false;
          
          console.error('==================== ERROR DETAILS ====================');
          console.error('Full Error Object:', error);
          
          // Better error handling
          if (error.response) {
            // Server responded with error status
            console.error('Response Status:', error.response.status);
            console.error('Response Data (Full):', error.response.data);
            console.error('Response Headers:', error.response.headers);
            
            // Try to extract error message
            let errorMsg = 'Unknown error';
            if (error.response.data) {
              if (typeof error.response.data === 'string') {
                errorMsg = error.response.data;
              } else if (error.response.data.message) {
                errorMsg = error.response.data.message;
              } else if (error.response.data.error) {
                errorMsg = error.response.data.error;
              } else {
                errorMsg = JSON.stringify(error.response.data);
              }
            }
            
            console.error('Extracted Error Message:', errorMsg);
            console.error('======================================================');
            
            if (error.response.status === 500) {
              obj.$message.error('Server Error (500): ' + errorMsg);
            } else if (error.response.status === 400) {
              obj.$message.error('Invalid Data (400): ' + errorMsg);
            } else if (error.response.status === 401) {
              obj.$message.error('Authentication Required (401): Please login again.');
            } else if (error.response.status === 403) {
              obj.$message.error('Access Denied (403): ' + errorMsg);
            } else {
              obj.$message.error('Error (' + error.response.status + '): ' + errorMsg);
            }
          } else if (error.request) {
            // Request made but no response
            console.error('No Response from Server');
            console.error('Request:', error.request);
            console.error('======================================================');
            obj.$message.error('No response from server. Please check your internet connection or server status.');
          } else {
            // Error in request setup
            console.error('Request Setup Error:', error.message);
            console.error('======================================================');
            obj.$message.error('Request Error: ' + error.message);
          }
        });
    
      }
    }
}

    
  </script>

<style scoped>
.outer_box {
  background: linear-gradient(160deg, #f0f4ff 0%, #e8eeff 40%, #f5f0ff 100%);
  min-height: 100vh;
  padding: 0 0 48px 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.all_out_box {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 28px;
  box-shadow: 0 12px 48px rgba(59,194,255,0.10), 0 4px 16px rgba(59,194,255,0.06);
  padding: 24px 30px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border: 1px solid rgba(59,194,255,0.06);
}
.tttiltleBox {
  margin-bottom: 36px;
}
.title-container {
  text-align: center;
}
.main-title {
  font-size: 2.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #3bc2ff, #0284c7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  text-align: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 0.2em 0.45em;
}
.main-title .icon-title,
.main-title .house-room-text,
.main-title .title-room-sep {
  -webkit-text-fill-color: #0284c7;
  color: #0284c7;
  background: none;
  background-clip: border-box;
  -webkit-background-clip: border-box;
}
.main-title .house-name-text {
  -webkit-text-fill-color: transparent;
  background: linear-gradient(135deg, #3bc2ff, #0284c7);
  -webkit-background-clip: text;
  background-clip: text;
}
.title-room-sep {
  margin: 0 0.1em;
  opacity: 0.55;
  font-weight: 700;
}
.house-room-text {
  font-weight: 800;
  max-width: 100%;
  word-break: break-word;
}
.title-underline {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #3bc2ff, #0284c7);
  border-radius: 2px;
  margin: 0 auto 8px auto;
}
.room-switcher {
  margin: 12px auto 4px auto;
  max-width: 280px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.switcher-label {
  font-size: 0.9rem;
  color: #3bc2ff;
  font-weight: 600;
  text-align: left;
}
.room-select {
  width: 100%;
}
.current-room-name {
  margin-top: 6px;
  text-align: center;
  font-size: 1rem;
  color: #4b5563;
}
.subtitle {
  color: #94a3b8;
  font-size: 1.1rem;
  margin-bottom: 0;
}
.top_rate {
  margin-bottom: 32px;
}
.top_rate_right {
  background: #fafaff;
  border-radius: 20px;
  box-shadow: 0 2px 16px rgba(59,194,255,0.07);
  padding: 28px 32px 24px 32px;
  border: 1px solid #e0f7ff;
}
.form-section {
  margin-bottom: 28px;
}
.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #3bc2ff;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.section-icon {
  font-size: 1.2rem;
}
.input-group {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 18px;
}
.input-field {
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
}
.form-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  font-size: 0.92rem;
}
.input-container {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  padding: 0 10px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.input-container:focus-within {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 3px rgba(59,194,255,0.12);
}
.custom-input.modern-input {
  border: 1.5px solid #e2e8f0;
  outline: none;
  background: #ffffff;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 1rem;
  color: #1e293b;
  flex: 1;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.custom-input.modern-input:focus {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 3px rgba(59,194,255,0.12);
}
.input-suffix {
  color: #7b8ca6;
  font-size: 1rem;
  margin-left: 6px;
}
.button-group {
  margin-top: 18px;
}
.button-container {
  margin: 30px;
  display: flex;
  gap: 12px;
}
.bed-type-button.modern-btn {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 999px;
  color: #64748b;
  font-weight: 500;
  font-size: 1rem;
  padding: 8px 24px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.bed-type-button.modern-btn.active {
  background: linear-gradient(135deg, #3bc2ff, #00b4d8);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(99,102,241,0.25);
}
.bed-type-button.modern-btn:hover, .bed-type-button.modern-btn:focus {
  border-color: #00b4d8;
  color: #3bc2ff;
  background: #eef2ff;
}
.tmp_rate {
  margin-bottom: 32px;
}
.rateBlock {
  background: #fafaff;
  border-radius: 20px;
  box-shadow: 0 2px 16px rgba(99,102,241,0.07);
  padding: 28px 32px 24px 32px;
  border: 1px solid #eef2ff;
}
.rating-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}
.rating-item {
  background: #ffffff;
  border-radius: 14px;
  border: 1.5px solid #e2e8f0;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: all 0.25s ease;
}
.rating-item:hover {
  border-color: #a5b4fc;
  box-shadow: 0 4px 16px rgba(99,102,241,0.10);
  transform: translateY(-2px);
}
.rating-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.icon-container {
  background: #eef2ff;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rating-label {
  font-weight: 600;
  color: #1e293b;
  font-size: 1.05rem;
}
.rating-hint {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-bottom: 2px;
  font-style: italic;
}
.el_rate_yes {
  margin-top: 6px;
}
.reviewBlock {
  background: #fafaff;
  border-radius: 20px;
  box-shadow: 0 2px 16px rgba(99,102,241,0.07);
  padding: 32px 28px;
  margin-top: 16px;
  border: 1px solid #eef2ff;
}
.review-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #4f46e5;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.review-description {
  color: #7b8ca6;
  font-size: 1.08rem;
  margin-bottom: 18px;
}
.upload-section {
  margin-bottom: 18px;
}
.upload-title {
  font-size: 1.08rem;
  color: #374151;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.upload-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.file-input-wrapper {
  position: relative;
  width: 180px;
  height: 48px;
}

.file-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;  /* 确保 input 元素覆盖整个容器 */
  left: 0;
  top: 0;
  cursor: pointer;
  z-index: 2;  /* 确保它处于最上层 */
}

.file-input-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #f5f3ff;
  border-radius: 10px;
  border: 2px dashed #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #3bc2ff;
  font-weight: 500;
  font-size: 1.08rem;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 1;
}

.file-input-overlay:hover {
  background: #eef2ff;
  border-color: #00b4d8;
  color: #4f46e5;
}

.file-input-wrapper.is-loading .file-input-overlay {
  background: #eef2ff;
  border-color: #a5b4fc;
  cursor: wait;
}

.file-input-wrapper.is-loading .file-input {
  cursor: wait;
}

.compress-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #c7d2fe;
  border-top: 3px solid #3bc2ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.image-gallery {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}
.image-preview {
  position: relative;
  width: 80px;
  height: 80px;
}
.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(74,144,226,0.10);
}
.delete-button {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #fee2e2;
  border: none;
  color: #ef4444;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s;
}
.delete-button:hover {
  background: #ef4444;
  color: #fff;
  transform: scale(1.1);
}
.review-textarea-container {
 
  margin-top: 18px;
  position: relative;
}
.review-textarea {
  width: 100%;
  min-height: 180px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  padding: 14px;
  font-size: 1.05rem;
  color: #1e293b;
  resize: vertical;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
}
.review-textarea:focus {
  outline: none;
  border-color: #00b4d8;
  box-shadow: 0 0 0 3px rgba(129,140,248,0.12);
}
.character-count {
  position: absolute;
  bottom: 8px;
  right: 16px;
  color: #7b8ca6;
  font-size: 0.98rem;
  pointer-events: none;
  background: transparent;
  z-index: 2;
}
.house-name-text {
  display: inline;
  word-break: break-word;
  white-space: normal;
  max-width: 100%;
  line-height: 1.2;
}
.review-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
  align-items: stretch;
}
.review-actions-row .draft-save-button,
.review-actions-row .submit-button {
  flex: 1 1 160px;
  margin-top: 0;
}
.draft-save-button {
  padding: 16px 0;
  border-radius: 14px;
  border: 2px solid #bae6fd;
  background: #f8fafc;
  color: #0c4a6e;
  font-weight: 700;
  font-size: 1.05rem;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.draft-save-button:hover:not(:disabled) {
  background: #e0f2fe;
  border-color: #3bc2ff;
}
.draft-save-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.submit-button {
  margin-top: 28px;
  width: 100%;
  padding: 16px 0;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #3bc2ff, #0284c7);
  color: #fff;
  font-weight: 700;
  font-size: 1.15rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(99,102,241,0.30);
  letter-spacing: 0.5px;
}
.submit-button:hover:not(:disabled), .submit-button:focus:not(:disabled) {
  background: linear-gradient(135deg, #22b7f8, #0284c7);
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(59,194,255,0.40);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.submit-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top: 3px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.button-text {
  font-size: 1.15rem;
}
.button-icon {
  font-size: 1.3rem;
}
.agreement-text {
  color: #7b8ca6;
  font-size: 0.98rem;
  margin-top: 18px;
  text-align: center;
}
.agreement-link {
  color: #3bc2ff;
  text-decoration: underline;
  transition: color 0.2s;
}
.agreement-link:hover {
  color: #4f46e5;
}
.review-consent-section {
  margin-top: 28px;
}
.review-consent-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
}
.review-consent-group:last-child {
  margin-bottom: 0;
}
.review-consent-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  cursor: pointer;
  font-size: 0.92rem;
  line-height: 1.5;
  color: #374151;
}
.review-consent-checkbox input[type="checkbox"] {
  flex: none;
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin: 1px 0 0;
  accent-color: #3bc2ff;
  cursor: pointer;
}
.review-consent-checkbox span {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}
.review-consent-link {
  color: #3bc2ff;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: color 0.2s ease, text-decoration-color 0.2s ease;
}
.review-consent-link:hover,
.review-consent-link:focus {
  color: #0284c7;
  text-decoration-color: currentColor;
}
@media (max-width: 900px) {
  .all_out_box {
    padding: 24px 8px 18px 8px;
    max-width: 99vw;
  }
}
@media (max-width: 700px) {
  .all_out_box {
    padding: 10px 2vw 8px 2vw;
    max-width: 99vw;
  }
  .top_rate_right, .rateBlock, .reviewBlock {
    padding: 14px 8px;
  }
  .main-title {
    font-size: 1.2rem;
  }
  .room-switcher {
    max-width: 100%;
    padding: 0 8px;
  }
  
  /* Input groups responsive for tablet */
  .input-group {
    flex-direction: column !important;
    gap: 12px !important;
  }
  
  .input-field {
    flex: 1 1 100% !important;
  }
  
  /* Button containers responsive */
  .button-container {
    flex-wrap: wrap !important;
    gap: 8px !important;
  }
  
  .bed-type-button {
    flex: 1 1 calc(50% - 4px) !important;
    min-width: 100px !important;
  }
  
  .toggle-group {
    flex-wrap: wrap !important;
  }
  
  .toggle-btn {
    flex: 1 !important;
    min-width: 70px !important;
  }

  .review-consent-checkbox {
    font-size: 0.88rem;
  }
}

@media (max-width: 480px) {
  .outer_box {
    margin-top: 0;
  }
  
  .all_out_box {
    margin: 0 auto !important;
    padding: 20px 16px 16px 16px !important;
    border-radius: 20px;
  }
  
  .tttiltleBox {
    margin-bottom: 20px !important;
  }
  
  .main-title {
    font-size: 1.4rem !important;
  }
  
  .house-name-text {
    font-size: 1.1rem !important;
  }
  
  .section-title {
    font-size: 1rem !important;
    margin-bottom: 12px !important;
  }
  
  .section-icon {
    font-size: 1rem !important;
  }
  
  .form-section {
    margin-bottom: 16px !important;
  }
  
  .top_rate_right {
    padding: 16px 12px !important;
    border-radius: 16px;
  }
  
  .input-group {
    flex-direction: column !important;
    gap: 12px !important;
    margin-bottom: 12px !important;
  }
  
  .input-field {
    flex: 1 1 100% !important;
  }
  
  .form-label {
    font-size: 0.85rem !important;
    margin-bottom: 6px !important;
  }
  
  .custom-input.modern-input {
    padding: 10px 10px !important;
    font-size: 14px !important;
    border-radius: 8px;
  }
  
  .input-container {
    padding: 0 8px !important;
    border-radius: 8px;
  }
  
  .input-prefix {
    font-size: 12px !important;
  }
  
  /* Button containers fully responsive */
  .button-container {
    flex-direction: column !important;
    gap: 8px !important;
  }
  
  .bed-type-button {
    flex: 1 1 100% !important;
    padding: 12px 8px !important;
    font-size: 13px !important;
    min-width: unset !important;
  }
  
  .btn-icon {
    font-size: 0.9rem !important;
    margin-right: 4px !important;
  }
  
  /* Toggle buttons responsive */
  .toggle-group {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 8px !important;
    width: 100%;
  }
  
  .toggle-btn {
    flex: 1 !important;
    padding: 10px 8px !important;
    font-size: 13px !important;
    border-radius: 8px;
    min-width: unset !important;
  }
  
  .toggle-btn.active {
    background: linear-gradient(135deg, #3bc2ff, #0284c7) !important;
    color: white !important;
  }
  
  /* Room switcher */
  .room-switcher {
    max-width: 100% !important;
    padding: 0 !important;
    margin: 12px auto !important;
  }
  
  .room-select {
    max-width: 100%;
  }
  
  .switcher-label {
    font-size: 0.85rem !important;
  }
  
  /* Modal dialogs */
  .add-room-dialog ::v-deep .el-dialog {
    width: 90vw !important;
    margin: 0 !important;
    max-height: 90vh !important;
    overflow-y: auto !important;
  }
  
  .add-room-dialog ::v-deep .el-dialog__header {
    padding: 16px 16px !important;
  }
  
  .add-room-dialog ::v-deep .el-dialog__title {
    font-size: 1.1rem !important;
  }
  
  .add-room-dialog ::v-deep .el-dialog__body {
    padding: 16px !important;
  }
  
  .add-room-fields {
    gap: 12px !important;
  }
  
  .add-room-field-group {
    gap: 6px !important;
  }
  
  .add-room-label {
    font-size: 0.85rem !important;
    margin-bottom: 6px !important;
  }
  
  /* Submit button */
  .submit-button {
    padding: 14px 16px !important;
    font-size: 14px !important;
    margin-top: 16px !important;
    width: 100%;
  }
  
  /* Character count overlay responsive */
  .character-count {
    font-size: 11px !important;
    padding: 6px 8px !important;
  }
}

.add-room-dialog ::v-deep .el-dialog {
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(59, 194, 255, 0.25), 0 8px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.add-room-dialog ::v-deep .el-dialog__header {
  padding: 24px 28px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.add-room-dialog ::v-deep .el-dialog__title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #0f172a;
}

.add-room-dialog ::v-deep .el-dialog__headerbtn {
  top: 24px;
  right: 28px;
}

.add-room-dialog ::v-deep .el-dialog__headerbtn .el-dialog__close {
  color: #64748b;
  font-size: 1.5rem;
}

.add-room-dialog ::v-deep .el-dialog__headerbtn:hover .el-dialog__close {
  color: #0f172a;
}

.add-room-dialog ::v-deep .el-dialog__body {
  padding: 28px;
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.add-room-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.add-room-detail-fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.add-room-field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.add-room-label {
  font-weight: 600;
  color: #0f172a;
  font-size: 0.95rem;
  margin-bottom: 8px;
}

.add-room-field {
  width: 100%;
}

.add-room-field ::v-deep .el-input__inner {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.2s ease;
}

.add-room-field ::v-deep .el-input__inner:focus {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 3px rgba(59, 194, 255, 0.1);
}

.add-room-field ::v-deep .el-select .el-input__inner {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.add-room-field ::v-deep .el-select .el-input__inner:focus {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 3px rgba(59, 194, 255, 0.1);
}

.add-room-error {
  color: #ef4444;
  font-size: 0.875rem;
  margin: 8px 0 0;
  padding: 12px 16px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
}

.add-room-dialog ::v-deep .el-dialog__footer {
  padding: 20px 28px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.add-room-dialog ::v-deep .el-button {
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.add-room-dialog ::v-deep .el-button--default {
  background: #ffffff;
  border: 2px solid #e2e8f0;
  color: #64748b;
}

.add-room-dialog ::v-deep .el-button--default:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

.add-room-dialog ::v-deep .el-button--primary {
  background: linear-gradient(135deg, #3bc2ff 0%, #0284c7 100%);
  border: none;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(59, 194, 255, 0.3);
}

.add-room-dialog ::v-deep .el-button--primary:hover {
  background: linear-gradient(135deg, #0284c7 0%, #075985 100%);
  box-shadow: 0 6px 20px rgba(59, 194, 255, 0.4);
  transform: translateY(-2px);
}

/* New form element styles for Task 6 */
.select-container {
  position: relative;
}
.select-container select.modern-select {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: none;
  background: #e7e7e7;
  font-size: 1rem;
  color: #222;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  box-shadow: inset 2px 2px 5px rgba(0,0,0,0.1),
              inset -2px -2px 5px rgba(255,255,255,0.9);
  outline: none;
}
.select-container select.modern-select:focus {
  box-shadow: inset 4px 4px 8px rgba(0,0,0,0.15),
              inset -4px -4px 8px rgba(255,255,255,0.9);
}
.select-container::after {
  content: '▾';
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #7b8ca6;
  pointer-events: none;
  font-size: 1rem;
}
.input-prefix {
  color: #7b8ca6;
  font-weight: 600;
  font-size: 0.95rem;
  margin-right: 4px;
  white-space: nowrap;
}
.custom-input.modern-input.with-prefix {
  padding-left: 6px;
}
.toggle-group {
  display: flex;
  gap: 8px;
}
.toggle-btn {
  flex: 1;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1.5px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.25s ease;
}
.toggle-btn.active {
  background: linear-gradient(135deg, #3bc2ff, #00b4d8);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(99,102,241,0.25);
}
.toggle-btn:hover:not(.active) {
  border-color: #00b4d8;
  color: #3bc2ff;
  background: #eef2ff;
}
.insight-section {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
.tag-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.tag-chip {
  padding: 7px 16px;
  border-radius: 999px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}
.tag-chip.active {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border-color: #00b4d8;
  color: #4338ca;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(99,102,241,0.12);
}
.tag-chip:hover:not(.active) {
  border-color: #a5b4fc;
  color: #3bc2ff;
  background: #f5f3ff;
}
</style>
